// hooks/useHorarios.ts
import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import type { Horario } from '@/components/types/typeCalendar';
import { DIAS_SEMANA } from '@/components/constants/dias';

// ============================================================
// FILTRO POR TEMPORADA
// idTemporada null = horario base. En Supabase el null se filtra
// con .is(), no con .eq(): con .eq() no casaría ninguna fila y el
// borrado previo al guardado no borraría nada.
// ============================================================
function filtrarPorTemporada(query: any, idTemporada: string | null) {
  return idTemporada
    ? query.eq('id_horario_temporada', idTemporada)
    : query.is('id_horario_temporada', null);
}

// ============================================================
// VALIDADOR REUTILIZABLE
// ============================================================
function validarHorariosLista(lista: any[]): string | null {
  const sorted = [...lista].sort((a, b) =>
    a.hora_inicio.localeCompare(b.hora_inicio)
  );

  for (let i = 0; i < sorted.length; i++) {
    const { hora_inicio, hora_fin } = sorted[i];

    if (!hora_inicio || !hora_fin) return 'Falta hora inicio/fin';
    if (hora_fin <= hora_inicio) return 'La hora de fin debe ser mayor';

    if (i < sorted.length - 1) {
      const siguienteInicio = sorted[i + 1].hora_inicio;
      if (hora_fin > siguienteInicio) return 'Los horarios se solapan';
    }
  }

  return null;
}

// ============================================================
// HORARIOS POR USUARIO (trabajador)
// ============================================================
export function useHorariosPorUsuario(
  idUsuario: string | null,
  idEmpresa: string,
  idTemporada: string | null = null
) {
  const [horarios, setHorarios] = useState<Horario[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Temporada a la que corresponden los horarios que hay ahora en el estado.
  // Sirve para que quien consuma el hook sepa si ya tiene los datos de la
  // temporada que ha pedido o todavia son los de la anterior.
  const [temporadaCargada, setTemporadaCargada] = useState<
    string | null | false
  >(false);

  const fetchHorarios = useCallback(async () => {
    if (!idUsuario || !idEmpresa) return [];

    setLoading(true);

    const { data, error } = await filtrarPorTemporada(
      supabase
        .from('vista_horario_trabajador')
        .select('id_horario, dia_semana, hora_inicio, hora_fin, id_horario_temporada')
        .eq('id_usuario', idUsuario)
        .eq('id_empresa', idEmpresa),
      idTemporada
    );

    setLoading(false);

    if (error) {
      Sentry.captureException(error, {
        extra: { idUsuario, idEmpresa, idTemporada },
      });
      toast.error('Error al cargar horarios por usuario');
      return [];
    }

    setHorarios(data as Horario[]);
    setTemporadaCargada(idTemporada);
    return data as Horario[];
  }, [idUsuario, idEmpresa, idTemporada]);

  useEffect(() => {
    fetchHorarios();
  }, [fetchHorarios]);

  const guardarHorariosTrabajador = async (
    horariosPorDia: Record<string, any[]>,
    usuarioSistemaId: string,
    idUsuarioTrabajador: string | null
  ) => {
    if (!idUsuarioTrabajador || !idEmpresa)
      return { ok: false, error: 'Faltan IDs' };

    setSaving(true);

    try {
      for (const [dia, lista] of Object.entries(horariosPorDia)) {
        const err = validarHorariosLista(lista);
        if (err) {
          toast.error(`Error en ${dia}: ${err}`);
          return { ok: false, error: err };
        }
      }

      const del = await filtrarPorTemporada(
        supabase
          .from('horario_trabajador')
          .delete()
          .eq('id_usuario', idUsuarioTrabajador)
          .eq('id_empresa', idEmpresa),
        idTemporada
      );

      if (del.error) throw del.error;

      const payload: any[] = Object.values(horariosPorDia)
        .flat()
        .map((e) => ({
          id_usuario: idUsuarioTrabajador,
          id_empresa: idEmpresa,
          id_horario_temporada: idTemporada,
          dia_semana: e.dia_semana,
          hora_inicio: e.hora_inicio,
          hora_fin: e.hora_fin,
          usuario_creacion: usuarioSistemaId,
        }));

      if (payload.length > 0) {
        const ins = await supabase.from('horario_trabajador').insert(payload);
        if (ins.error) throw ins.error;
      }

      await fetchHorarios();

      return { ok: true };
    } catch (error) {
      Sentry.captureException(error, {
        extra: { idUsuarioTrabajador, idEmpresa, idTemporada },
      });
      toast.error('No se pudieron guardar los horarios del trabajador');
      return { ok: false, error };
    } finally {
      setSaving(false);
    }
  };

  return {
    horarios,
    loading,
    saving,
    temporadaCargada,
    refetch: fetchHorarios,
    guardarHorariosTrabajador,
  };
}

// ============================================================
// HORARIOS POR EMPRESA
// ============================================================
export function useHorariosPorEmpresa(
  idEmpresa: string,
  idTemporada: string | null = null
) {
  const [horarios, setHorarios] = useState<Horario[]>([]);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchHorarios = useCallback(async () => {
    if (!idEmpresa) return [];

    setLoadingFetch(true);

    const { data, error } = await filtrarPorTemporada(
      supabase
        .from('vista_horario_empresa')
        .select('id_horario, dia_semana, hora_inicio, hora_fin, id_horario_temporada')
        .eq('id_empresa', idEmpresa),
      idTemporada
    );

    setLoadingFetch(false);

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa, idTemporada } });
      toast.error('Error al cargar horarios por empresa');
      return [];
    }

    setHorarios(data as Horario[]);
    return data as Horario[];
  }, [idEmpresa, idTemporada]);

  useEffect(() => {
    fetchHorarios();
  }, [fetchHorarios]);

  const guardarHorariosEmpresa = async (
    horariosPorDia: Record<number, any[]>
  ) => {
    setSaving(true);

    try {
      const del = await filtrarPorTemporada(
        supabase.from('horario_empresa').delete().eq('id_empresa', idEmpresa),
        idTemporada
      );

      if (del.error) throw del.error;

      const payload = Object.values(horariosPorDia)
        .flat()
        .map((f) => ({
          id_empresa: idEmpresa,
          id_horario_temporada: idTemporada,
          dia_semana: DIAS_SEMANA[f.dia_semana],
          hora_inicio: f.hora_inicio,
          hora_fin: f.hora_fin,
          activo: true,
        }));

      if (payload.length > 0) {
        const ins = await supabase.from('horario_empresa').insert(payload);
        if (ins.error) throw ins.error;
      }

      await fetchHorarios();
      toast.success('Horarios guardados correctamente');
      return { ok: true };
    } catch (error) {
      Sentry.captureException(error, { extra: { idEmpresa, idTemporada } });
      toast.error('No se pudieron guardar los horarios');
      return { ok: false, error };
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setHorarios([]);

  return {
    horarios,
    loadingFetch,
    saving,
    guardarHorariosEmpresa,
    refrescar: fetchHorarios,
    reset,
  };
}

// ============================================================
// DÍAS LABORABLES DE CADA TRABAJADOR DE LA EMPRESA
// Devuelve { id_usuario: Set('lunes','sábado',...) } para poder
// calcular días de ausencia según el horario real de cada uno,
// en vez de asumir que el fin de semana no se trabaja.
//
// Solo mira el horario base: sin filtrar por temporada, un día
// contaría como laborable por estar en cualquiera de ellas.
// ============================================================
export function useDiasLaborablesPorEmpresa(idEmpresa: string) {
  const [diasPorUsuario, setDiasPorUsuario] = useState<
    Record<string, Set<string>>
  >({});

  useEffect(() => {
    if (!idEmpresa) return;

    const fetchDias = async () => {
      const { data, error } = await supabase
        .from('vista_horario_trabajador')
        .select('id_usuario, dia_semana')
        .eq('id_empresa', idEmpresa)
        .is('id_horario_temporada', null);

      if (error) {
        Sentry.captureException(error, { extra: { idEmpresa } });
        return;
      }

      const map: Record<string, Set<string>> = {};
      (data ?? []).forEach((h: any) => {
        const dia = h.dia_semana?.trim().toLowerCase();
        if (!dia) return;
        (map[h.id_usuario] ??= new Set()).add(dia);
      });

      setDiasPorUsuario(map);
    };

    fetchDias();
  }, [idEmpresa]);

  return diasPorUsuario;
}

// ============================================================
// HORARIO VIGENTE FECHA A FECHA
// Delega en horario_rango: resuelve la temporada de cada dia, y en las
// fechas sin temporada devuelve el horario base. Sin idUsuario, empresa.
// ============================================================
export interface FranjaFecha {
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
}

export function useHorarioRango(
  idEmpresa: string,
  idUsuario: string | null,
  desde: string | null,
  hasta: string | null
) {
  const [franjas, setFranjas] = useState<FranjaFecha[]>([]);
  const [cargado, setCargado] = useState(false);

  const fetchRango = useCallback(async () => {
    if (!idEmpresa || !desde || !hasta) return;

    const { data, error } = await supabase.rpc('horario_rango', {
      p_id_empresa: idEmpresa,
      p_desde: desde,
      p_hasta: hasta,
      p_id_usuario: idUsuario,
    });

    if (error) {
      Sentry.captureException(error, {
        extra: { idEmpresa, idUsuario, desde, hasta },
      });
      toast.error('Error al cargar el horario');
      setFranjas([]);
      setCargado(true);
      return;
    }

    setFranjas(
      (data as any[]).map((f) => ({
        fecha: f.fecha,
        hora_inicio: f.hora_inicio,
        hora_fin: f.hora_fin,
      }))
    );
    setCargado(true);
  }, [idEmpresa, idUsuario, desde, hasta]);

  useEffect(() => {
    fetchRango();
  }, [fetchRango]);

  return { franjas, cargado };
}